import React, {
  useRef,
  useState,
} from 'react';
import {
  Attachments,
  Sender,
} from '@ant-design/x';
import {
  Button,
  GetRef,
  Popover,
  Space,
  Flex,
  Tooltip,
} from 'antd';
import {
  ArrowUpOutlined,
  ClearOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import type { AttachmentsRef } from '@/types/antdx';
import {
  XAiSenderProps,
} from '@/types';
import { useChatbotContext } from '@/hooks/useProviderContext';
import { useStyles } from './styles';

const XAiSender: React.FC<XAiSenderProps> = (props) => {
  // 使用新的 Hook 来处理 Provider 上下文
  const { mergedProps } = useChatbotContext(props);
  const {
    footerTips,
    clearBtnShow,
    value,
    onChange,
    onClear,
    onStop,
    onSubmit,
    files,
    enableUpload,
    onChangeFiles,
    uploadRequest,
    loading: propLoading = false,
    disabled,
    ...resetProps
  } = mergedProps as XAiSenderProps;
  const senderRef = useRef<GetRef<typeof Sender>>(null);
  const attachmentsRef = useRef<AttachmentsRef>(null);

  // 如果在 Provider 中，使用 Provider 的状态
  const loading = mergedProps.loading || propLoading;
  const [clearPopoverVisible, setClearPopoverVisible] = useState(false); // 气泡卡片显隐藏
  const styles = useStyles();

  // 触发发送
  const handleSend = () => {
    if (value && value.trim()) {
      // 直接使用合并后的 onSend，自动处理 Provider 和独立模式
      onSubmit?.({
        text: value,
        files: files || [],
      });
    }
  };

  // 文件上传按钮引用
  const fileInputRef = useRef<HTMLInputElement>(null);

  //  点击清除按钮
  const handleClear = () => {
    setClearPopoverVisible(true);
  };

  // 取消清除
  const cancelClear = () => {
    setClearPopoverVisible(false);
  };

  // 确认清除
  const confirmClear = () => {
    onClear?.();
    setClearPopoverVisible(false);
  };

  // 清空按钮提示文案
  const clearConfirmContent = (
    <div className={styles.p('2px')}>
      <p className={clsx(styles.mb(14), styles.mt(0))}>确认清除所有聊天记录并重置会话！</p>
      <div className={styles.popoverFooter}>
        <Button size="small" onClick={cancelClear}>
          取消
        </Button>
        <Button size="small" type="primary" danger onClick={confirmClear}>
          确认
        </Button>
      </div>
    </div>
  );

  // 自定义上传按钮
  const uploadButton = (
    <div className={styles.flexCenterGap2}>
      <Tooltip
        placement="top"
        title="支持图片、文档等上传"
      >
        <Button
          color="default"
          variant="text"
          shape="circle"
          size="small"
          icon={<PlusCircleOutlined style={{ fontSize: 20, color: '#949494' }} />}
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current?.click();
            }
          }}
        />
        <input
          type="file"
          ref={fileInputRef}
          multiple
          onChange={(e) => {
            const fileList = e.target.files || [];
            if (!fileList.length) return;
            for (let i = 0; i < fileList.length; i++) {
              const file = fileList[i];
              attachmentsRef.current?.upload(file);
            }
            e.target.value = '';
          }}
          style={{ display: 'none' }}
          accept="*/*"
        />
      </Tooltip>
    </div>
  );

  return (
    <>
      <div className={styles.chatFooter}>
        <div className={clsx(styles.flex, styles.gap(8))}>
          {/* 普通文本输入 */}
          <Sender
            {...resetProps}
            value={value}
            className={styles.sendWrapper}
            placeholder="继续对话..."
            disabled={disabled}
            loading={loading}
            ref={senderRef}
            header={enableUpload && (
              <Sender.Header
                closable={false}
                forceRender
                open={!!files?.length}
                styles={{
                  content: { padding: 0 },
                }}
              >
                <Attachments
                  ref={attachmentsRef}
                  customRequest={uploadRequest}
                  multiple
                  items={files}
                  // imageProps={{
                  //   preview: {
                  //     mask: true,
                  //   },
                  // }}
                  onChange={({ fileList }) => {
                    onChangeFiles?.(fileList);
                  }}
                  getDropContainer={() => senderRef.current?.nativeElement}
                />
              </Sender.Header>
            )}
            suffix={false}
            footer={(_, info) => {
              const { SendButton, LoadingButton } = info.components;
              return (
                <Flex justify="space-between">
                  <div>
                    {/* 清空按钮 */}
                    {clearBtnShow && (
                      <Popover
                        content={clearConfirmContent}
                        title="确认清空对话"
                        trigger="click"
                        open={clearPopoverVisible}
                        onOpenChange={setClearPopoverVisible}
                      >
                        <div
                          className={styles.clearBtn}
                          onClick={handleClear}
                        >
                          <ClearOutlined className={styles.text(12)} />
                        </div>
                      </Popover>
                    )}
                  </div>
                  <Space size="small" style={{ display: 'flex', alignItems: 'center'}}>
                    {enableUpload && (
                      <>
                        {uploadButton}
                        <div className={styles.divider} />
                      </>
                    )}
                    {loading
                      ? (
                        <LoadingButton onClick={() => onStop?.()} className={styles.stopButton} />
                      )
                      : (
                        <SendButton
                          className={styles.sendButton}
                          type="primary"
                          icon={<ArrowUpOutlined className={styles.text(20)} />}
                          disabled={disabled}
                        />
                      )}
                  </Space>
                </Flex>
              );
            }}
            onChange={(v) => {
              onChange?.(v);
            }}
            onSubmit={handleSend}
            onCancel={() => {
              onStop?.();
            }}
          />
        </div>
      </div>
      { footerTips && <div className={styles.footerTips}>{footerTips}</div>}
    </>
  );
};

export default XAiSender;
